import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function formatCount(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

function formatPct(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toLocaleString("ru-RU", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

function formatDay(value) {
  if (!value) return "";
  const [, month, day] = String(value).slice(0, 10).split("-");
  return day && month ? `${day}.${month}` : value;
}

function eachIsoDay(from, to) {
  const days = [];
  if (!from || !to || from > to) return days;
  const current = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    const day = String(current.getDate()).padStart(2, "0");
    days.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function MetricCard({ label, value, hint }) {
  return (
    <article className="dash-metric">
      <p className="dash-metric-label">{label}</p>
      <p className="dash-metric-value">{value}</p>
      {hint ? <p className="dash-metric-hint">{hint}</p> : null}
    </article>
  );
}

function ChartTooltip({ active, payload, label, isPercent }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value;
  const formatValue = isPercent ? formatPct : formatCount;
  return (
    <div className="dash-chart-tooltip">
      <p className="dash-chart-tooltip-date">{label}</p>
      <p>
        <strong>{value == null ? "нет отчёта" : formatValue(value)}</strong>
      </p>
    </div>
  );
}

function PeriodChart({
  title,
  dateFrom,
  dateTo,
  series,
  dataKey,
  isPercent,
  latest,
}) {
  const formatValue = isPercent ? formatPct : formatCount;
  const byDate = Object.fromEntries((series || []).map((item) => [item.date, item]));
  const data = eachIsoDay(dateFrom, dateTo).map((date) => {
    const row = byDate[date];
    const raw = row ? row[dataKey] : null;
    return {
      date,
      label: formatDay(date),
      value: raw == null || raw === "" ? null : Number(raw),
    };
  });
  if (!data.length) return null;

  return (
    <section className="dash-panel dash-side-chart">
      <div className="dash-panel-head">
        <h2>{title}</h2>
        {latest != null ? (
          <span className="dash-panel-total">{formatValue(latest)}</span>
        ) : null}
      </div>
      <div className="dash-side-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={isPercent ? (value) => formatPct(value) : formatCount}
              width={isPercent ? 52 : 48}
              allowDecimals={isPercent}
            />
            <Tooltip
              content={(props) => (
                <ChartTooltip {...props} isPercent={isPercent} />
              )}
            />
            <Bar
              dataKey="value"
              fill="#8aa0b8"
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

const CHARTS = [
  { key: "sick", title: "На больничном" },
  { key: "vacation", title: "В отпуске" },
  { key: "minors_pct", title: "% несовершеннолетних", isPercent: true },
  { key: "nonresidents_pct", title: "% нерезидентов", isPercent: true },
  { key: "headcount", title: "Списочная численность" },
];

const HrDashboard = ({ summary }) => {
  const totals = summary.totals || {};
  const latest = summary.latest || {};
  const series = summary.series || [];
  const snapshotHint = summary.snapshot_date
    ? `на ${formatDay(summary.snapshot_date)}`
    : "последний день с отчётом";

  return (
    <div className="dash-hr">
      <section className="dash-metrics">
        <MetricCard
          label="Принято"
          value={formatCount(totals.hired)}
          hint="сумма за период"
        />
        <MetricCard
          label="Уволено"
          value={formatCount(totals.fired)}
          hint="сумма за период"
        />
      </section>

      <div className="dash-hr-charts">
        {CHARTS.map((chart) => (
          <PeriodChart
            key={chart.key}
            title={chart.title}
            dateFrom={summary.date_from}
            dateTo={summary.date_to}
            series={series}
            dataKey={chart.key}
            isPercent={Boolean(chart.isPercent)}
            latest={latest[chart.key]}
          />
        ))}
      </div>

      <section className="dash-legend">
        <h2>Как считаем</h2>
        <p>
          Берём загрузки за выбранные даты
          {summary.loaded_dates?.length
            ? ` (есть отчёты за ${summary.loaded_dates
                .map(formatDay)
                .join(", ")})`
            : ""}
          . Пустые ячейки считаем как 0. Число в заголовке графика — снимок{" "}
          {snapshotHint}.
        </p>
        <dl>
          <div>
            <dt>Принято и уволено</dt>
            <dd>
              Складываем значения из каждого дневного отчёта за период. Это
              поток за выбранные дни, а не численность на дату.
            </dd>
          </div>
          <div>
            <dt>Графики по дням</dt>
            <dd>
              Больничные, отпуск, проценты и списочная численность не
              суммируем: каждый столбец — значение из отчёта за этот день.
              День без загрузки оставляем пустым. Проценты из файла 0,167
              показываем как 16,7% и не складываем между днями.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
};

export default HrDashboard;
